
import { Component, Watch, Prop, Mixins } from "vue-property-decorator";
import { namespace, Getter, Mutation, State } from "vuex-class";
import vSelect3 from "vselect3";
import { EventDetails, EventShareUser, NotifyNotificationRules } from "@/store/site-monitor/types";
import { EventOutcome, EventOutcomeResponse } from "@/types/EventOutcome";
import api from "@/services/api.service";
import eventsApi from "@/services/api.event.service";
import CloseEventResultDTO from "@/types/sv-data/events/CloseEventResultDTO";
import CloseMultipleEventsDTO from "@/types/sv-data/events/CloseMultipleEventsDTO";
import ActionState from "@/types/sv-data/events/ActionState";
import { UserPermissions } from "@/store/types";
import NestedSelect from "@/components/utilities/NestedSelect.vue";
import AutoFeedAlarms from "./event-queue/AutoFeedAlarms.vue";
import { AlarmQueueFilterID } from "@/store/eventqueue/types";
import RegionalHandlerMixin from "@/mixins/RegionalHandlerMixin";
import VirtualAssistSummary from "@/components/ai-virtual-operator/VirtualAssistSummary.vue";
import CloseMultipleEventsResultDTO from "@/types/sv-data/events/CloseMultipleEventsResultDTO";
import { NotificationOptionsType } from "@/types/NotificationOptionsType";

const SiteMonitor = namespace("siteMonitor");
const SiteMonitorCameras = namespace("siteMonitorCameras");
const Eventqueue = namespace("eventqueue");
const Tasks = namespace("tasks");
const Map = namespace("map");
const ExternalMapLayers = namespace("externalMapLayers");
const AreaNotes = namespace("areaNotes");

@Component({
	components: {
		"v-select-3": vSelect3,
		"nested-select": NestedSelect,
		"auto-feed-alarms": AutoFeedAlarms,
		"virtual-assist-summary": VirtualAssistSummary,
	},
})
export default class EventClose extends Mixins(RegionalHandlerMixin) {
	$refs!: {
		eventCloseModal: any;
	};

	private bypassRequiredActionsConfirmed: boolean = false;
	private showBypassRequiredActionsConfirmation: boolean = false;

	@State private autoFeedAlarms: boolean;
	@Eventqueue.Getter("getActiveFilterID") currentFilterID!: AlarmQueueFilterID;

	@SiteMonitor.Getter getEventShare: EventShareUser[];
	@SiteMonitor.Getter("getEventCloseShown") eventCloseShown!: boolean;
	@SiteMonitor.Getter getIsUploadingFile: boolean;
	@SiteMonitor.Getter getEventDetails: EventDetails;
	@SiteMonitor.Getter("eventOutcomesFlatList") private outcomesFlatList: EventOutcome[];

	@SiteMonitor.Mutation setEventCloseShown: (shown: boolean) => void;
	@SiteMonitor.Mutation setEventDetails: (details: EventDetails) => void;
	@SiteMonitor.Mutation setEventNoPermission: (noPermission: boolean) => void;
	@SiteMonitor.Mutation resetEventRecords: () => void;
	@SiteMonitor.Mutation setEventRecordsErrorState: (unAckedEventRecords: number[], unRestoredEventRecords: number[]) => void;
	@SiteMonitor.Mutation resetSiteMonitorState: () => void;
	@SiteMonitor.Mutation setNotifyNotificationRules: (notifyNotificationRules: NotifyNotificationRules) => void;

	@SiteMonitorCameras.Mutation("resetState") resetCamerasState: () => void;
	@Tasks.Mutation("resetState") resetTasksState: () => void;
	@Eventqueue.Mutation("resetState") resetEventQueueState: () => void;
	@Map.Mutation("resetState") resetMapState: () => void;
	@ExternalMapLayers.Mutation("resetState") resetExternalMapLayersState: () => void;
	@AreaNotes.Mutation("resetState") resetAreaNotesState: () => void;

	@SiteMonitor.Action("loadOutcomesForEvent") loadOutcomes: (eventId: number) => Promise<EventOutcomeResponse>;
	@SiteMonitor.Action loadAllOutcomesForAlarms: () => Promise<EventOutcomeResponse>;
	@SiteMonitor.Action("closeEvent") closeEvent: (data: any) => Promise<CloseEventResultDTO>;
	@SiteMonitor.Action("closeMultipleEvents") closeEvents: (
		data: CloseMultipleEventsDTO
	) => Promise<CloseMultipleEventsResultDTO>;
	@SiteMonitor.Action private fetchEventDetails: (eventId: number) => Promise<EventDetails>;
	@SiteMonitor.Action private loadNotificationRules: ({eventId, eventOutcomeId, outcomeNote}) => Promise<boolean>;

	@Getter getFeature: (featuresList: string[]) => boolean;
	@Getter getPermissions: UserPermissions;

	@Tasks.Getter hasEventOutcomeTask: boolean;
	@Tasks.Getter getOutstandingRequiredTaskCount: number;

	@Eventqueue.Getter("getSelectedAlarms") selectedAlarms: number[];
	@Eventqueue.Getter("getClosingAction") closingAction: ActionState;
	@Eventqueue.Mutation removeSelectedAlarm: (data: number) => void;
	@Eventqueue.Mutation setRefreshQueue: (refresh: boolean) => void;
	@Eventqueue.Mutation setClosingAction: (action: ActionState) => void;

	@Getter getUserTenantGroupId: number;
	@Getter getUserSuiteTenantId: string;

	@Mutation setReloadEventQueue: (boolean) => void;

	@Prop(Number) eventid?: number;
	@Prop({ default: true }) singleEvent: boolean;

	@Prop({ type: Boolean, required: false, default: false })
	private updateEventOutcome: boolean;

	private focusTimeout: NodeJS.Timeout | null = null;

	private isDestroyed: boolean = false;

	public eventRecords: any[] = [];
	public errors: any;

	public lastRecord: number = 0;

	// tree of all possible outcomes, of unpredictable depth, as received by API
	public outcomeTree: any[] = [];
	// to allow for infinite depth in outcomes tree above, this array maintains currrent
	// breadcrumbs path of selections made by the user, and is used as a support datastructre
	public outcomeSelectionPath: EventOutcome[] = [];
	// is iterated by template to display
	public outcomeSelectionControls: any[] = [];

	public clearGuards: boolean = false;
	public outcomeNote: string = "";
	public eventCloseError: string | null = null;

	public get otherUserCount() {
		const SharedUsers = this.getEventShare.filter((user: any) => user.isActive).length;
		return SharedUsers - 1;
	}

	public get currentSelectedOutcome() {
		const lastOutcome = this.outcomeSelectionPath[this.outcomeSelectionPath.length - 1];
		if (lastOutcome == null) return "";

		return lastOutcome.title;
	}

	public get isInvalidCategoryWarning(): boolean {
		return this.eventOutcome == null && this.updateEventOutcome;
	}

	/*
	 * Getter to validate if the current event outcome note meets the required criteria.
	 */
	private get isOutcomeNoteValid(): boolean {
		return (
			!this.getEventDetails?.eventNoteRequired ||
			(this.getEventDetails?.eventNoteRequired && this.outcomeNote.length > 0) ||
			(!!this.updateEventOutcome && this.outcomeNote.length > 0)
		);
	}

	private get isEventClosed(): boolean {
		return this.getEventDetails?.eventClosed;
	}

	private get eventOutcome(): EventOutcome | null {
		return this.outcomeSelectionPath.length === 0
			? null
			: this.outcomeSelectionPath[this.outcomeSelectionPath.length - 1];
	}

	private get isNotificationRulesEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "Email", "NotificationTemplates", "NotificationRules"]);
	}

	private get hasNotificationRulePermissions(): boolean {
		return this.getPermissions.canViewNotificationRules || this.getPermissions.canEditNotificationRules;
	}

	private get canUseNotificationRules(): boolean {
		return this.isNotificationRulesEnabled && this.hasNotificationRulePermissions;
	}

	private get isPreserveEventOutcomeEnabled(): boolean {
		return this.getFeature(["Alarms", "SiteMonitor", "PersistEventOutcome"]);
	}

	public created(): void {
		this.resetErrors();
		this.setEventCloseShown(false);
	}

	public beforeDestroy(): void {
		this.isDestroyed = true;
		if (this.focusTimeout != null) {
			clearTimeout(this.focusTimeout);
			this.focusTimeout = null;
		}
	}

	public resetData(): void {
		this.eventCloseError = null;
		this.outcomeNote = "";
		this.setClosingAction(ActionState.Undefined);
		this.resetErrors();
	}

	private resetErrors(): void {
		this.eventCloseError = null;
		this.errors = {
			http: {
				getOutcomes: null,
				closeEvent: null,
			},
			form: {
				isValid: null,
				outcomeChoice: {
					message: null,
					isValid: null,
				},
				outcomeNote: {
					message: null,
					isValid: null,
				},
			},
		};
	}

	@Watch("eventCloseShown")
	public async onEventCloseShownChanged(value: boolean, oldValue: boolean): Promise<void> {
		if (value) {
			// Event Close modal load
			if (!!this.eventid && !this.isDestroyed) {
				await this.fetchEventDetails(this.eventid);
			}

			if (!this.isDestroyed){
				await this.loadOutcomesForEvent();
			}

			this.resetData();

			if (this.useEventOutcomeTask || this.getEventDetails?.eventOutcomeNote) {
				this.outcomeNote = this.getEventDetails?.eventOutcomeNote ?? "";
			}

			this.$refs.eventCloseModal.show();

			// Wait for the next tick - the modal should be visible then
			await this.$nextTick();

			if (!this.updateEventOutcome) {
				const outcomeSelect0 = (this.$refs["outcomeSelect0"] as any)?.[0];
				if (this.focusTimeout != null) {
					clearTimeout(this.focusTimeout);
				}
				this.focusTimeout = setTimeout(() => {
					outcomeSelect0?.$el.querySelector("input").focus();
					this.focusTimeout = null;
				}, 0.5 * this.$config.ANIMATION_DURATION);
			} else {
				// Sets the outcome not when updateEventOutcome feature is enabled
				this.outcomeNote = this.getEventDetails?.eventOutcomeNote ?? "";
			}
		} else {
			// Only update when feature is enabled and not updating event outcome via the Event Search Update flag.
			if (this.isPreserveEventOutcomeEnabled && !this.updateEventOutcome && this.singleEvent && this.getEventDetails && !this.isEventClosed) {
				await eventsApi.preserveEventOutcome(this.eventid!, this.eventOutcome?.eventOutcomeID!, this.outcomeNote);
			}

			this.$refs.eventCloseModal.hide();
		}
	}

	private onSummaryGenerated(summary: string): void {
		this.outcomeNote = summary;
	}

	public mounted(): void {
		this.isDestroyed = false;
	}

	public async loadOutcomesForEvent(): Promise<void> {
		let response: EventOutcomeResponse = null;
		if (this.singleEvent) {
			response = await this.loadOutcomes(this.eventid);
		} else {
			response = await this.loadAllOutcomesForAlarms();
		}

		this.outcomeTree = response.eventOutcomeTree;

		if (this.updateEventOutcome || this.getEventDetails?.eventOutcomeID > 0) {
			this.outcomeSelectionPath = [];
			this.buildOutcomePath();
		}
	}

	public async onConfirmClicked(): Promise<void> {
		if (!this.validate()) {
			return;
		}

		var triggerReload: boolean = false;
		if (this.errors.form.isValid) {
			this.eventCloseError = null;
			let closureResult: CloseEventResultDTO = null;

			this.setClosingAction(ActionState.InProcess);

			if (this.updateEventOutcome) {
				await api.updateClosedEventOutcome({
					eventId: this.eventid,
					eventOutcomeId: this.eventOutcome?.eventOutcomeID,
					note: this.outcomeNote,
				});
				this.$emit("updatedEventOutcome");
				this.outcomeNote = "";
				this.setEventCloseShown(false);
			} else if (this.singleEvent) {
				if (
					this.getOutstandingRequiredTaskCount > 0 &&
					this.canBypassRequiredActions &&
					!this.bypassRequiredActionsConfirmed
				) {
					this.showBypassRequiredActionsConfirmation = true;
					return;
				}

				closureResult = await this.closeSingleEvent();

				if (
					this.isReloadEventQueueEnabled &&
					this.getEventDetails?.groupID &&
					(await api.getReloadEventQueuePref(this.getEventDetails?.groupID))
				) {
					triggerReload = true;
				}
			} else {
				return await this.closeMultipleEvents();
			}

			if (closureResult != null) {
				if (closureResult.success === true) {
					this.setClosingAction(ActionState.Success);

					let closeEndEventModal = false;

					if (this.singleEvent && this.canUseNotificationRules) {
						const eventOutcomeId = this.outcomeSelectionPath[this.outcomeSelectionPath.length - 1].eventOutcomeID;
						if (eventOutcomeId === null || eventOutcomeId === undefined) {
							return;
						}

						closeEndEventModal = await this.loadNotificationRules({
							eventId: this.eventid,
							eventOutcomeId: eventOutcomeId,
							outcomeNote: this.outcomeNote
						});
					}

					if (closeEndEventModal) {
						this.setEventDetails({... this.getEventDetails, eventClosed: true})
						this.setEventNoPermission(false);
						this.setEventCloseShown(false);
						return;
					}

					this.setEventDetails(null);
					this.resetEventRecords();

					this.setEventCloseShown(false);

					if (this.singleEvent) {
						if (!this.isAutoFeedAlarmsEnabled || (this.isAutoFeedAlarmsEnabled && !this.autoFeedAlarms)) {
							this.setReloadEventQueue(triggerReload);
							this.$router.push({ path: "/event-queue" });
						} else {
							this.setPendingEventPickup({
								highestPriorityAlarm: true,
								pickupMethod: "auto feed",
								currentFilterID: this.currentFilterID.filterId,
								allowInProcessing: false,
								openInNewWindow: false,
							});
							await this.processTopAlarm(this.currentFilterID, "auto feed", true);
							this.$router.go(0);
						}
					}
				} else {
					this.eventCloseError = closureResult.error;

					if (this.singleEvent) {
						this.setEventRecordsErrorState(closureResult.eventRecordIdsAwaitingAcknowledgement, closureResult.eventRecordIdsAwaitingRestore);
					}

					this.setClosingAction(ActionState.Fail);

					if (!this.eventCloseShown)
					{
						this.$notify({
							type: "error",
							title: "Failed to close event",
							text: this.eventCloseError
						});
						console.error(`Failed to close event - ${this.eventCloseError}`);
					}
				}
			}
		}
	}

	private async closeMultipleEvents(): Promise<void> {
		const payload: CloseMultipleEventsDTO = {
			eventIds: this.selectedAlarms,
			eventOutcomeId: this.eventOutcome!.eventOutcomeID,
			outcomeNote: this.outcomeNote,
		};

		const result: CloseMultipleEventsResultDTO = await this.closeEvents(payload);
		this.setRefreshQueue(true);

		if (result != null) {
			if (result.success === true) {
				this.setClosingAction(ActionState.Success);
				this.setEventDetails(null);
				this.resetEventRecords();
				this.setEventCloseShown(false);
			} else {
				this.eventCloseError = result.error;
				this.setClosingAction(ActionState.Fail);

				if (!this.eventCloseShown)
				{
					this.$notify({
						type: NotificationOptionsType.error,
						title: "Failed to close multiple events",
						text: this.eventCloseError
					});
				}

				console.error(`Failed to close multiple events - ${this.eventCloseError}, Details:  ${JSON.stringify(result.failureReasonForEventIds)}`);
			}
		}
	}

	private async closeSingleEvent(): Promise<CloseEventResultDTO> {
		const payload = {
			eventId: this.eventid,
			eventOutcomeId: this.eventOutcome!.eventOutcomeID,
			outcomeNote: this.outcomeNote,
			clearGuards: true,
			bypassRequiredActions: this.bypassRequiredActionsConfirmed,
		};

		return this.closeEvent(payload);
	}

	public cancelCloseEvent(): void {
		this.setEventCloseShown(false);
	}

	private bypassActionsConfirmed(): void {
		this.showBypassRequiredActionsConfirmation = false;
		this.bypassRequiredActionsConfirmed = true;
		this.onConfirmClicked();
	}

	private cancelBypassActionsConfirmation(): void {
		this.showBypassRequiredActionsConfirmation = false;
		this.setClosingAction(ActionState.Undefined); // Required to reset confirmation button contents from spinner icon to text
	}

	// @todo - ensure that in future we use a validation library (vuelidate), makes doing this stuff
	// a thousand times simpler!!
	private validate() {
		this.errors.form.isValid = true;

		if (!this.eventOutcome) {
			return false;
		}

		// selected outcome is leaf node of possible outcomes tree
		if (this.eventOutcome.childOutcomes && this.eventOutcome.childOutcomes.length > 0) {
			this.errors.form["outcomeChoice"].message = "Please select a specific outcome";
			this.errors.form["outcomeChoice"].isValid = false;
			this.errors.form.isValid = false;
		} else {
			this.errors.form["outcomeChoice"].message = null;
			this.errors.form["outcomeChoice"].isValid = null;
		}

		// outcome note less than 5000 symbols, optional
		if (this.outcomeNote.length > 5000) {
			this.errors.form["outcomeNote"].message = "Note too long - max length of 5000 chars allowed";
			this.errors.form["outcomeNote"].isValid = false;
			this.errors.form.isValid = false;
		} else if ((this.updateEventOutcome || !this.singleEvent) && this.outcomeNote.length == 0) {
			this.errors.form["outcomeNote"].message = "Note is required";
			this.errors.form["outcomeNote"].isValid = false;
			this.errors.form.isValid = false;
		} else {
			this.errors.form["outcomeNote"].message = null;
			this.errors.form["outcomeNote"].isValid = null;
		}

		return this.errors.form.isValid;
	}

	private isLeafNode(choice: EventOutcome) {
		return choice.childOutcomes !== undefined && choice.childOutcomes.length === 0 ? true : false;
	}

	private getParent(nodeOutcome: any, findOutcome: any) {
		if (nodeOutcome.childOutcomes != null) {
			let foundOutcome = null;
			nodeOutcome.childOutcomes.forEach((outcome: any) => {
				if (outcome.eventOutcomeID == findOutcome.eventOutcomeID) {
					foundOutcome = nodeOutcome;
				}
			});

			if (foundOutcome != null) {
				return foundOutcome;
			}

			nodeOutcome.childOutcomes.forEach((outcome: any) => {
				let recurseOutcome = this.getParent(outcome, findOutcome);
				if (recurseOutcome != null) {
					foundOutcome = recurseOutcome;
				}
			});

			if (foundOutcome != null) {
				return foundOutcome;
			}
		}

		return null;
	}

	private buildOutcomePath(): void {
		if (this.getEventDetails.eventOutcomeID) {
			var IdToFind = this.getEventDetails.eventOutcomeID;
			for (var outcome of this.outcomeTree) {
				// If we have the path setup
				if (this.checkOutcomeInPath(outcome, IdToFind)) {
					this.outcomeSelectionPath = [outcome, ...this.outcomeSelectionPath];
					// Finish
					return;
				}
			}
		}

		this.outcomeSelectionPath = [];
	}

	private checkOutcomeInPath(outcome: any, idToFind: number): boolean {
		if (outcome.eventOutcomeID == idToFind) {
			return true;
		}

		if (!!outcome.childOutcomes && outcome.childOutcomes.length > 0) {
			for (var child of outcome.childOutcomes) {
				if (this.checkOutcomeInPath(child, idToFind)) {
					this.outcomeSelectionPath = [child, ...this.outcomeSelectionPath];
					return true;
				}
			}
		}

		return false;
	}

	private get useEventOutcomeTask(): boolean {
		return !this.updateEventOutcome && this.getFeature(["Actions", "EventOutcome"]) && this.hasEventOutcomeTask;
	}

	private get modalTitle(): string {
		var closeText = this.singleEvent ? "Close" : "End Events";
		return this.updateEventOutcome ? "Change Category" : closeText;
	}

	private get confirmBtnText(): string {
		return this.singleEvent ? "OK" : "Confirm";
	}

	private get isEventOutcomeValid(): boolean {
		return this.validate() && this.isOutcomeNoteValid;
	}

	private get isClosingInProcess(): boolean {
		return this.closingAction == ActionState.InProcess;
	}

	private get isClosingSuccess(): boolean {
		return this.closingAction == ActionState.Success;
	}

	private get isClosingFail(): boolean {
		return this.closingAction == ActionState.Fail;
	}

	private get isVirtualAssistSummaryEnabled(): boolean {
		return (
			this.getFeature(["AI", "VirtualAssist"]) && this.getPermissions.canUseVirtualAssistSummary && this.singleEvent
		);
	}

	private get enforceModalButtonDismissal(): boolean {
		return this.getFeature(["TenantSettings", "EnforceDismissingModalByButtons"]);
	}

	private get isReloadEventQueueEnabled(): boolean {
		return this.getFeature(["Temp_EventReload"]);
	}

	private get isAutoFeedAlarmsEnabled(): boolean {
		return this.getFeature(["Alarms", "EventQueue", "AutoFeedNextAlarm"]);
	}

	private get canBypassRequiredActions(): boolean {
		return (
			this.getFeature(["Alarms", "SiteMonitor", "BypassRequiredActions"]) &&
			this.getPermissions.canBypassRequiredActions
		);
	}
}
